/*
 * AI chats in the quick switcher (S13 §2.7): Ctrl/Cmd+K finds the reader's chats by title or key, and
 * "new" offers *New AI chat*. Each row says, after the title, what Claude may do there and how the
 * chat stands — "Can change code · Needs you" — the words its row on the start page uses.
 *
 * The provider answers from the module's slice and never from the network: the switcher asks on
 * every keystroke without a debounce, so a provider that went to the server would spend a request per
 * letter (S-10-D16). The rows stand under a heading of their own, because a
 * chat is neither a conversation nor a person.
 *
 * This file is reached from the entry chunk through the module object: it holds no screen, and the
 * words of a row (its mode and headline) are read on the first query, not with the entry.
 */

import type { QuickSwitcherProvider } from "../../../core/modules/registry.ts"
import { scoreTarget } from "../../../core/shell/index.ts"
import { client } from "../../../core/store/store.ts"
import { aiChatHref, aiNewHref } from "../../../routes/links.ts"
import { t } from "../i18n/t.ts"
import { aichatSlice } from "../sync/slice.ts"
import type { ChatRow } from "../wire/aichat.gen.ts"

/** How many of this module's rows one query offers. */
const MAX_ROWS = 20

/** The heading this module's rows stand under, as a key of its own catalogue. */
const GROUP = "aichat.switcher.group"

/** How well a chat answers a term: by its key's start first, then by its title. */
function scoreOf(row: ChatRow, title: string, term: string): number | null {
	const needle = term.trim().toLocaleLowerCase("en-GB")
	if (needle === "") return 0
	if (row.key.startsWith(needle)) return 1000
	return scoreTarget(needle, title)
}

export const aichatQuickSwitcher: QuickSwitcherProvider = {
	id: "aichat",
	async query(term: string) {
		const [{ headlineModel }, { modeLabel }, { peopleOf }] = await Promise.all([
			import("../common/headline.ts"),
			import("../common/mode.ts"),
			import("../people/names.ts"),
		])
		const model = client.model
		const slice = aichatSlice(model)
		const people = peopleOf(model)
		const found = Object.values(slice.chats)
			.map((row) => {
				const title = slice.titles[row.chatId] ?? row.key
				return { row, title, score: scoreOf(row, title, term) }
			})
			.filter((hit): hit is { row: ChatRow; title: string; score: number } => hit.score !== null)
			.sort((a, b) => b.score - a.score || b.row.lastActivityAt - a.row.lastActivityAt)
			.slice(0, MAX_ROWS)
			.map(({ row, title }) => ({
				id: `aichat.${row.key}`,
				label: title,
				detail: `${modeLabel(row.mode)} · ${headlineModel(row, people).label}`,
				to: aiChatHref(row.key),
				groupTitleKey: GROUP,
				icon: "aichat",
			}))
		const needle = term.trim().toLocaleLowerCase("en-GB")
		const offerNew =
			needle !== "" && t("aichat.switcher.new").toLocaleLowerCase("en-GB").includes(needle)
		return offerNew
			? [
					{
						id: "aichat.new",
						label: t("aichat.switcher.new"),
						to: aiNewHref(),
						groupTitleKey: GROUP,
						icon: "plus",
					},
					...found,
				]
			: found
	},
}
